/**
 * Calypso Button Animation
 * Only applies when calypso is selected in theme settings
 * Wraps button text in span tags for the animation to work
 */

// Function to wrap button text in a simple span
function wrapButtonTextForCalypso(button) {
  // Skip if already processed
  if (button.dataset.calypsoProcessed) return;

  // Skip if button already has a span child (preserve existing structure)
  const existingSpan = button.querySelector(':scope > span');
  if (existingSpan) {
    button.dataset.calypsoProcessed = 'true';
    return;
  }

  // Get text content
  const textContent = button.textContent.trim();
  if (!textContent) return;

  // Create wrapper span
  const span = document.createElement('span');

  // Move all child nodes into the span
  while (button.firstChild) {
    span.appendChild(button.firstChild);
  }

  // Add span back to button
  button.appendChild(span);
  button.dataset.calypsoProcessed = 'true';
}

// Function to check if element should be excluded
function shouldExcludeFromCalypso(element) {
  // Exclude if already processed
  if (element.dataset.calypsoProcessed) return true;

  // Exclude header and navigation elements
  if (element.closest('header, nav, .header, .menu-drawer, [role="navigation"]')) return true;

  // Exclude quantity buttons
  if (element.closest('.quantity, quantity-input, [class*="quantity"]')) return true;
  if (element.classList.contains('quantity__button')) return true;

  // Exclude close buttons and modal controls
  if (element.classList.contains('modal__close') || element.classList.contains('modal__toggle')) return true;
  if (element.getAttribute('aria-label')?.toLowerCase().includes('close')) return true;

  // Exclude popup/modal triggers
  if (element.classList.contains('product-popup-modal__button')) return true;
  if (element.hasAttribute('aria-controls')) return true;

  // Exclude if no meaningful text
  const textContent = element.textContent.trim();
  if (!textContent || textContent.length === 0) return true;

  // Exclude icon-only buttons
  const svgs = element.querySelectorAll('svg, img, .icon, [class*="icon-"]');
  if (svgs.length > 0 && textContent.length < 3) return true;

  return false;
}

// Function to apply calypso ONLY when the body class is present
function initializeGlobalCalypso() {
  const body = document.body;

  // CRITICAL: Only run if calypso is the selected animation
  const hasLinkCalypso = body.classList.contains('link-btns--calypso');
  const hasActionCalypso = body.classList.contains('action-btns--calypso');

  // If calypso is not selected for either button type, don't do anything
  if (!hasLinkCalypso && !hasActionCalypso) {
    return;
  }

  // Only process link buttons if link-btns--calypso is active
  if (hasLinkCalypso) {
    const linkButtons = document.querySelectorAll(
      'a.button:not([class*="header"]):not([class*="menu"])'
    );

    linkButtons.forEach(button => {
      if (!shouldExcludeFromCalypso(button)) {
        wrapButtonTextForCalypso(button);
      }
    });
  }

  // Only process action buttons if action-btns--calypso is active
  if (hasActionCalypso) {
    const actionButtons = document.querySelectorAll(
      '.button:not(.quantity__button):not(.product-popup-modal__button), ' +
      'button[type="submit"]:not(.quantity__button), ' +
      'button[type="button"]:not(.quantity__button):not(.product-popup-modal__button)'
    );

    actionButtons.forEach(button => {
      if (!shouldExcludeFromCalypso(button)) {
        wrapButtonTextForCalypso(button);
      }
    });
  }
}

// Run on DOMContentLoaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeGlobalCalypso);
} else {
  initializeGlobalCalypso();
}

// Observe for AJAX-loaded content - but only if calypso is active
if (typeof MutationObserver !== 'undefined') {
  const observer = new MutationObserver((mutations) => {
    const body = document.body;

    // Check if calypso is still the selected animation
    if (!body.classList.contains('link-btns--calypso') &&
        !body.classList.contains('action-btns--calypso')) {
      return; // Don't process if calypso is not selected
    }

    let shouldReinitialize = false;

    mutations.forEach((mutation) => {
      if (mutation.addedNodes.length > 0) {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            if (node.matches && (node.matches('.button, button') ||
                node.querySelector && (node.querySelector('.button, button')))) {
              shouldReinitialize = true;
            }
          }
        });
      }
    });

    if (shouldReinitialize) {
      setTimeout(initializeGlobalCalypso, 100);
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
}
